import {
  afterNextRender,
  ApplicationRef,
  DOCUMENT,
  Directive,
  ElementRef,
  inject,
} from '@angular/core';
import { DIALOG_SELECTOR, topmostDialog } from './dialog-dom';

/**
 * Opt-out marker: Enter pressed anywhere inside an element carrying this attribute is
 * left to that element (e.g. a chips-style multi autocomplete that adds a value on Enter).
 */
export const BD_UI_DIALOG_ENTER_IGNORE_ATTRIBUTE =
  'data-bd-dialog-enter-ignore';

/**
 * Focus targets that already give Enter a meaning of their own. Enter pressed there must
 * not additionally trigger the dialog's primary action.
 */
const OWNS_ENTER_SELECTOR = [
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  'button',
  'a[href]',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="tab"]',
  '[role="treeitem"]',
  '[role="combobox"][aria-expanded="true"]',
  // Search fields: Enter means "search", never "confirm the dialog".
  'input[type="search"]',
  '[role="searchbox"]',
  `[${BD_UI_DIALOG_ENTER_IGNORE_ATTRIBUTE}]`,
].join(',');

/** Input of a multi-value autocomplete (chips): typed text there is not committed yet. */
const MULTI_VALUE_INPUT_SELECTOR = '.p-autocomplete-input-multiple input';

/** Fields that must be filled before the dialog can be confirmed. */
const REQUIRED_FIELD_SELECTOR = '[required], [aria-required="true"]';

/** Elements that can take keyboard focus. */
const FOCUSABLE_SELECTOR =
  'input, select, textarea, button, [tabindex]:not([tabindex="-1"]), [contenteditable]:not([contenteditable="false"])';

/** Set on the dialog once one marked button has taken over its initial focus. */
const FOCUS_CLAIMED_ATTRIBUTE = 'data-bd-dialog-focus-claimed';

/**
 * Marks the primary action of a dialog so that pressing Enter triggers it.
 *
 * Put it on the `pButton` (or `p-button`) that saves / confirms the dialog:
 *
 * ```html
 * <button pButton severity="primary" bdDialogPrimaryAction (click)="ref.close(part)">
 * ```
 *
 * The button is clicked (so its `(click)` handler is the single source of truth) only when
 * - it sits in the topmost open dialog (same z-index rule PrimeNG uses for Escape),
 * - it is enabled (a disabled / loading button swallows Enter) and visible (a button in an
 *   inactive stepper step is skipped, so each step can mark its own primary action),
 * - Enter is plain (no modifier, not auto-repeated, not part of an IME composition),
 * - no focused control consumed Enter already (`defaultPrevented`, e.g. an open select)
 *   and the focus target does not own Enter (textarea, buttons, links, open comboboxes,
 *   search fields, a multi-value autocomplete with typed but uncommitted text, anything
 *   inside `[data-bd-dialog-enter-ignore]`).
 *
 * It also sets the dialog's initial focus: the first required field (`required` /
 * `aria-required`), otherwise the primary action if it is enabled, otherwise PrimeNG's
 * default (first focusable element).
 *
 * Escape needs no directive: PrimeNG closes every closable dialog on Escape.
 */
@Directive({
  selector: '[bdDialogPrimaryAction]',
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)',
    '(document:focusin)': 'onDocumentFocusIn($event)',
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
  },
})
export class BdUiDialogPrimaryActionDirective {
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _document = inject(DOCUMENT);
  private readonly _appRef = inject(ApplicationRef);
  /** True once the initial focus of this dialog has been decided (by us or the user). */
  private _initialFocusSettled = false;

  constructor() {
    // PrimeNG may have focused inside the dialog before this directive existed.
    afterNextRender(() => {
      const active = this._document.activeElement;
      if (active && this._ownDialog()?.contains(active)) {
        this._settleInitialFocus(active);
      }
    });
  }

  /**
   * PrimeNG focuses the first focusable element once the dialog has opened. The first
   * focus that lands in this dialog is redirected to {@link initialFocusTarget}.
   */
  protected onDocumentFocusIn(event: FocusEvent): void {
    const target = event.target;
    if (
      !this._initialFocusSettled &&
      target instanceof Element &&
      this._ownDialog()?.contains(target)
    ) {
      this._settleInitialFocus(target);
    }
  }

  /** The user clicked into the dialog before PrimeNG focused: leave their focus alone. */
  protected onDocumentPointerDown(event: PointerEvent): void {
    const target = event.target;
    if (target instanceof Element && this._ownDialog()?.contains(target)) {
      this._initialFocusSettled = true;
    }
  }

  private _settleInitialFocus(current: Element): void {
    this._initialFocusSettled = true;
    const dialog = this._ownDialog();
    const button = this._button();
    // One marked button per dialog decides (stepper steps / alternative states).
    if (
      !dialog ||
      !button ||
      isHidden(button) ||
      dialog.hasAttribute(FOCUS_CLAIMED_ATTRIBUTE)
    ) {
      return;
    }
    dialog.setAttribute(FOCUS_CLAIMED_ATTRIBUTE, '');
    const target = initialFocusTarget(dialog, button);
    if (target && target !== current) {
      target.focus();
    }
  }

  private _ownDialog(): Element | null {
    return this._host.nativeElement.closest(DIALOG_SELECTOR);
  }

  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (!isPlainEnter(event) || event.defaultPrevented) {
      return;
    }

    const dialog = this._host.nativeElement.closest(DIALOG_SELECTOR);
    if (!dialog || dialog !== topmostDialog(this._document)) {
      return;
    }

    const target = event.target;
    if (target instanceof Element && target !== this._document.body) {
      if (
        !dialog.contains(target) ||
        target.closest(OWNS_ENTER_SELECTOR) ||
        hasUncommittedText(target)
      ) {
        return;
      }
    }

    // Change detection is zoneless (scheduled, not synchronous). A value committed by
    // this very keydown (p-inputnumber commits on Enter) or typed just before would
    // otherwise leave `[disabled]` one render behind and let an invalid state submit.
    this._appRef.tick();

    const button = this._button();
    if (!button || isDisabled(button) || isHidden(button)) {
      return;
    }

    // Also stops a second marked button in the same dialog from firing.
    event.preventDefault();
    button.click();
  }

  private _button(): HTMLButtonElement | null {
    const host = this._host.nativeElement;
    return host instanceof HTMLButtonElement
      ? host
      : host.querySelector('button');
  }
}

function isPlainEnter(event: KeyboardEvent): boolean {
  return (
    event.key === 'Enter' &&
    !event.repeat &&
    !event.isComposing &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey
  );
}

/**
 * Typed text in a multi-value autocomplete is not part of the value yet. Saving on Enter
 * would silently drop it, so Enter stays with the field until the text is committed or
 * cleared. (Single-value fields show the committed value as text, so they are not covered.)
 */
function hasUncommittedText(target: Element): boolean {
  return (
    target instanceof HTMLInputElement &&
    target.matches(MULTI_VALUE_INPUT_SELECTOR) &&
    target.value.trim() !== ''
  );
}

/**
 * Initial focus rule: the first required field; if there is none, the enabled primary
 * action; otherwise `null` (keep PrimeNG's default: the first focusable element).
 */
export function initialFocusTarget(
  dialog: Element,
  primary: HTMLButtonElement | null,
): HTMLElement | null {
  for (const candidate of Array.from(
    dialog.querySelectorAll<HTMLElement>(REQUIRED_FIELD_SELECTOR),
  )) {
    const field = candidate.matches(FOCUSABLE_SELECTOR)
      ? candidate
      : candidate.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (field && isUsable(field)) {
      return field;
    }
  }
  return primary && isUsable(primary) ? primary : null;
}

function isUsable(element: HTMLElement): boolean {
  const disabled =
    ('disabled' in element && element.disabled === true) ||
    element.getAttribute('aria-disabled') === 'true';
  return !disabled && !isHidden(element);
}

function isDisabled(button: HTMLButtonElement): boolean {
  return button.disabled || button.getAttribute('aria-disabled') === 'true';
}

/**
 * A marked button the user cannot see (inactive stepper step, collapsed panel, hidden tab)
 * is not the action Enter means. Environments without `checkVisibility` count as visible.
 */
function isHidden(element: HTMLElement): boolean {
  return (
    typeof element.checkVisibility === 'function' && !element.checkVisibility()
  );
}
